/**
 * InquiriesPage — 나의 전시장 > 상담문의 관리 (고객상담 요청접수관리 설계 §7)
 *
 * 카드 구조 안내: D-09 표준의 `ItemListRow`는 행 전체가 `<button>`이라, 이 화면처럼
 * 한 행 안에 전화 링크(`<a>`)·상태변경(`<select>`)이 동시에 필요한 경우(버튼 중첩은 무효한 HTML)
 * 쓸 수 없다. 그래서 카드는 `.item-list` 컨테이너 대신 커스텀 마크업(Tailwind, D-08 토큰 클래스)으로
 * 구성했다 — 자세한 사유는 갭노트 문서 참조.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge, DateRangePicker, EmptyState, ErrorState, FilterChip, Page, PageHeader, Search, Select, Skeleton,
} from '@indipia/ui-core';
import type { DateRangeValue } from '@indipia/ui-core';
import {
  fetchCustomerInquiries,
  fetchCustomerInquiryStats,
  updateCustomerInquiryStatus,
} from '../api/customer-inquiries';
import type { CustomerInquiryPagedList, CustomerInquiryStatus } from '../api/customer-inquiries';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

type DatePreset = 'TODAY' | 'WEEK' | 'MONTH' | 'CUSTOM';

const STATUS_TABS: Array<{ key: CustomerInquiryStatus | undefined; label: string }> = [
  { key: undefined, label: '전체' },
  { key: 'NEW', label: '신규' },
  { key: 'CONTACTED', label: '연락함' },
  { key: 'SCHEDULED', label: '상담예약' },
  { key: 'DONE', label: '완료' },
];

const STATUS_LABEL: Record<CustomerInquiryStatus, string> = {
  NEW: '신규',
  CONTACTED: '연락함',
  SCHEDULED: '상담예약',
  DONE: '완료',
};

const THREE_HOURS_MS = 3 * 60 * 60 * 1000;

/** 로컬 날짜 기준 — toISOString()은 UTC라 KST 오전 9시 이전엔 하루 전 날짜가 나온다 */
function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** BE는 분 단위로 내려주지만(avgResponseMinutes), 응답 지연이 며칠씩 쌓이면 분 단위 숫자가
 *  너무 커져 한눈에 안 들어와 시간 단위로 환산해 보여준다(소수 1자리). */
function formatAvgResponseHours(minutes: number | null | undefined): string {
  if (minutes == null) return '-';
  return (minutes / 60).toFixed(1);
}

function presetRange(preset: DatePreset, custom: DateRangeValue): { from?: string; to?: string } {
  if (preset === 'CUSTOM') {
    return { from: custom.start ? toDateStr(custom.start) : undefined, to: custom.end ? toDateStr(custom.end) : undefined };
  }
  const to = new Date();
  const from = new Date();
  if (preset === 'WEEK') from.setDate(from.getDate() - 7);
  if (preset === 'MONTH') from.setDate(from.getDate() - 30);
  return { from: toDateStr(from), to: toDateStr(to) };
}

function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /Android|iPhone|iPad|iPod/i.test(ua) || (navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua));
}

function elapsedLabel(createdAt: string): string {
  const diffMs = Date.now() - new Date(createdAt).getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours < 1) return '방금 전';
  if (hours < 24) return `${hours}시간 전`;
  return `${Math.floor(hours / 24)}일 전`;
}

/**
 * StatCard — 기획서 "B.고객상담신청함" 상단 통계 카드(.stat-card) 스타일 그대로.
 * ui-core StartCard(아이콘+가로배치)와 달리 숫자가 위, 라벨이 아래인 세로형 카드다 —
 * StartCard는 WorkLogPage 등 다른 화면과 공유하는 컴포넌트라 여기서만 쓰는 이 모양으로
 * 바꾸면 그쪽 화면이 깨지므로, 이 화면 전용으로 로컬 마크업을 따로 둔다.
 */
function StatCard({ label, count, unit, urgent }: { label: string; count: React.ReactNode; unit?: string; urgent?: boolean }) {
  return (
    <div
      className={urgent ? 'rounded-xl border px-4 py-3.5' : 'rounded-xl border border-border bg-surface-raised px-4 py-3.5'}
      style={urgent ? { background: 'var(--color-errorSubtle)', borderColor: 'var(--color-error)' } : undefined}
    >
      <div
        className={urgent ? 'text-[22px] font-black leading-none tracking-tight' : 'text-[22px] font-black leading-none tracking-tight text-text'}
        style={urgent ? { color: 'var(--color-error)' } : undefined}
      >
        {count}
        {unit && <span className="ml-0.5 text-body-sm font-bold text-text-muted">{unit}</span>}
      </div>
      <div className="mt-1.5 text-caption text-text-muted">{label}</div>
    </div>
  );
}

/** 상담 내용 — 실제로 2줄을 넘겨 잘릴 때만 "더보기"를 보여준다(짧은 메시지는 버튼 자체가 안 뜸). */
function InquiryMessage({ message }: { message: string }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el) setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [message]);

  return (
    <>
      <p ref={ref} className={`text-body-sm leading-relaxed text-text ${expanded ? '' : 'line-clamp-2'}`}>
        {message}
      </p>
      {overflowing && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-caption font-semibold text-point"
        >
          {expanded ? '접기' : '더보기'}
        </button>
      )}
    </>
  );
}

export function InquiriesPage() {
  const queryClient = useQueryClient();

  const [datePreset, setDatePreset] = useState<DatePreset>('WEEK');
  const [customRange, setCustomRange] = useState<DateRangeValue>({ start: null, end: null });
  const [status, setStatus] = useState<CustomerInquiryStatus | undefined>(undefined);
  const [searchInput, setSearchInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const mobile = useMemo(isMobileDevice, []);

  const debouncedSearch = useDebouncedValue(searchInput, 300);
  useEffect(() => { setKeyword(debouncedSearch.trim()); }, [debouncedSearch]);

  const { from, to } = presetRange(datePreset, customRange);

  const listParams = { status, keyword: keyword || undefined, dateFrom: from, dateTo: to, page: 0, size: 50 };
  const listQueryKey = ['customer-inquiries', 'list', listParams] as const;

  const { data: listData, isLoading, isError, refetch } = useQuery({
    queryKey: listQueryKey,
    queryFn: () => fetchCustomerInquiries(listParams),
  });

  const { data: stats } = useQuery({
    queryKey: ['customer-inquiries', 'stats'],
    queryFn: fetchCustomerInquiryStats,
  });

  const tabCountQueries = useQueries({
    queries: STATUS_TABS.map((tab) => ({
      queryKey: ['customer-inquiries', 'count', tab.key, from, to],
      queryFn: () => fetchCustomerInquiries({ status: tab.key, dateFrom: from, dateTo: to, page: 0, size: 1 }),
    })),
  });

  const statusMutation = useMutation({
    mutationFn: ({ inquiryId, status: newStatus }: { inquiryId: number; status: CustomerInquiryStatus }) =>
      updateCustomerInquiryStatus(inquiryId, newStatus),
    onMutate: async ({ inquiryId, status: newStatus }) => {
      await queryClient.cancelQueries({ queryKey: listQueryKey });
      const previous = queryClient.getQueryData<CustomerInquiryPagedList>(listQueryKey);
      if (previous) {
        queryClient.setQueryData<CustomerInquiryPagedList>(listQueryKey, {
          ...previous,
          content: previous.content.map((item) =>
            item.inquiryId === inquiryId ? { ...item, inquiryStatus: newStatus } : item),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(listQueryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-inquiries'] });
    },
  });

  const items = listData?.content ?? [];

  return (
    <Page width="md">
      <PageHeader title="상담문의" subtitle="공개 전시장에서 들어온 상담문의를 확인하고 응대 상태를 관리하세요" />

      <div className="space-y-4">

        {/* 통계 4종 (GET /inquiries/stats) */}
        <div className="grid grid-cols-4 gap-3">
          <StatCard label="이번주 신규 문의" count={stats?.newThisWeek ?? 0} unit="건" />
          <StatCard label="3시간 이상 미응답" count={stats?.overdueNew ?? 0} unit="건" urgent />
          <StatCard label="응답률" count={stats ? Math.round(stats.responseRate) : 0} unit="%" />
          <StatCard label="평균 응답시간" count={formatAvgResponseHours(stats?.avgResponseMinutes)} unit={stats?.avgResponseMinutes != null ? '시간' : undefined} />
        </div>

        {/* 기간 프리셋 + 검색 */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-text-muted">기간</span>
          <FilterChip active={datePreset === 'TODAY'} size="sm" onClick={() => setDatePreset('TODAY')}>오늘</FilterChip>
          <FilterChip active={datePreset === 'WEEK'} size="sm" onClick={() => setDatePreset('WEEK')}>1주일</FilterChip>
          <FilterChip active={datePreset === 'MONTH'} size="sm" onClick={() => setDatePreset('MONTH')}>1개월</FilterChip>
          {/* 직접입력 — 기간을 고르면 CUSTOM 으로 전환, active 가 프리셋/직접설정 중 적용 쪽을 표시 (§15.14) */}
          <DateRangePicker
            size="sm"
            presets={false}
          placeholder="직접입력"
            value={customRange}
            active={datePreset === 'CUSTOM'}
            onChange={(v) => { setCustomRange(v); setDatePreset('CUSTOM'); }}
            aria-label="직접입력 기간"
          />
          <div className="ml-auto w-56 min-w-0">
            <Search value={searchInput} onChange={setSearchInput} size="sm" placeholder="이름·연락처·사례명 검색" />
          </div>
        </div>

        {/* 상태 탭(건수) */}
        <div className="flex flex-wrap gap-1.5">
          {STATUS_TABS.map((tab, i) => (
            <FilterChip
              key={tab.label}
              active={status === tab.key}
              count={tabCountQueries[i]?.data?.totalElements ?? 0}
              onClick={() => setStatus(tab.key)}
            >
              {tab.label}
            </FilterChip>
          ))}
        </div>

        {/* 로딩 */}
        {isLoading && (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => <Skeleton key={i} height={96} className="rounded-xl" />)}
          </div>
        )}

        {/* 오류 */}
        {!isLoading && isError && (
          <ErrorState
            title="상담문의를 불러오지 못했어요"
            description="잠시 후 다시 시도해 주세요."
            onRetry={() => refetch()}
          />
        )}

        {/* 빈 상태 */}
        {!isLoading && !isError && items.length === 0 && (
          <EmptyState
            icon="📭"
            title="아직 들어온 상담문의가 없어요"
            description="공개 전시장에 시공사례를 올리면 고객이 남긴 상담문의가 여기에 모입니다."
          />
        )}

        {/* 목록 — 정렬은 서버 고정(상태우선순위→등록일), 클라이언트 재정렬 없음 */}
        {!isLoading && !isError && items.length > 0 && (
          <ul className="space-y-2">
            {items.map((item) => {
              const overdue = item.inquiryStatus === 'NEW'
                && Date.now() - new Date(item.createdAt).getTime() >= THREE_HOURS_MS;
              return (
                <li key={item.inquiryId} className="rounded-md border border-border bg-surface-raised px-3 py-2.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-body-sm font-bold text-text">{item.customerName}</span>
                        <span className="text-caption text-text-muted">{item.customerPhone}</span>
                        {item.sourceRefTitle && <Badge status="info">{item.sourceRefTitle}</Badge>}
                      </div>
                      {item.inquiryMessage && <InquiryMessage message={item.inquiryMessage} />}
                      <div className="flex items-center gap-2 text-caption text-text-muted">
                        <span>{elapsedLabel(item.createdAt)}</span>
                        {overdue && <span className="font-semibold text-danger">빠른 응답 필요</span>}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <a
                        href={mobile ? `tel:${item.customerPhone}` : undefined}
                        aria-disabled={!mobile}
                        title={mobile ? '전화 연결' : '모바일에서 전화 연결'}
                        className={`btn btn-etc btn-sm ${mobile ? '' : 'pointer-events-none opacity-40'}`}
                      >
                        전화
                      </a>
                      <Select
                        size="sm"
                        value={item.inquiryStatus}
                        aria-label="상태 변경"
                        onChange={(e) => statusMutation.mutate({
                          inquiryId: item.inquiryId,
                          status: e.target.value as CustomerInquiryStatus,
                        })}
                      >
                        {(Object.entries(STATUS_LABEL) as Array<[CustomerInquiryStatus, string]>).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </Select>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Page>
  );
}
